import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { toast } from "../../../components/AppToast";
import { fetchSkuMaster } from "../../../services/item/item";
import { fetchDataAndPatchFields } from "../../../services/item/item";
import {
  createPricingFoundation,
  updatePricingFoundation,
} from "../../../services/pricingFoundation/pricingFoundation";
import { getCustomerListByTenantAPI } from "../../../services/customer/customer";
import { fetchCustomerGroupList } from "../../../services/customer-group/customerGroup";
import { normalizeCustomerRecord } from "../../tenant/organization-management/customerModel";
import "./pricingFoundation.css";

const PLACEHOLDER_SELECT = "Select…";

export default function CreatePricingFoundation({ onClose, onSuccess, editData, readOnly = false }) {
  const isEdit = !!editData && !readOnly;
  const userDetail = useSelector((state) => state?.login?.userDetail);
  const resolvedTenantId = userDetail?.tenantId?._id ?? userDetail?.tenantId ?? "";
  
  const [formData, setFormData] = useState({
    skuid: editData?.skuid || editData?.skuId || "",
    description: editData?.description || "",
    originalPrice: editData?.originalPrice || editData?.original_price || "",
    sellingPrice: editData?.sellingPrice || editData?.selling_price || "",
    customer: editData?.customer || editData?.sellerOrganization || editData?.seller_organization || "",
    customerGroup:
      editData?.customerGroup ||
      editData?.customer_group ||
      "",
  });

  const [skuOptions, setSkuOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerGroupSelectOptions, setCustomerGroupSelectOptions] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingCustomerGroups, setLoadingCustomerGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Load SKU options
  const loadSkuOptions = useCallback(async () => {
    setLoadingSkus(true);
    try {
      const res = await fetchSkuMaster(0, 1000); // Load more for dropdown
      const list = Array.isArray(res) ? res : res?.data || res?.list || res?.results || [];
      const options = list.map((item) => {
        const h = item.header || item;
        const skuId = h.skuId ?? h.sku_id ?? item.skuId ?? item.sku_id ?? "";
        const description = h.description ?? h.skuDescription ?? item.description ?? item.skuDescription ?? "";
        return {
          value: skuId,
          label: `${skuId} - ${description}`,
          description,
          details: item,
        };
      }).filter((o) => o.value);
      setSkuOptions(options);
    } catch (err) {
      console.error("Error loading SKUs:", err);
      toast.error("Failed to load SKU options");
    } finally {
      setLoadingSkus(false);
    }
  }, []);

  // Load customer options (GET tenant/organization/customers/by-tenant); UI labels use "Supplier"
  const loadCustomerOptions = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      if (!resolvedTenantId) {
        console.warn("No tenant id on user detail; cannot load customers by tenant");
        setCustomerOptions([]);
        return;
      }
      const list = await getCustomerListByTenantAPI(resolvedTenantId);
      const options = list.map((item) => {
        const c = normalizeCustomerRecord(item, "");
        const value = String(c._id || c.customerId || c.id || "").trim();
        const name = String(item.customerName ?? c.customerName ?? "").trim();
        const code = String(item.customerCode ?? c.customerCode ?? "").trim();
        const label = name
          ? code
            ? `${name} (${code})`
            : name
          : code || value || "Supplier";
        return { value, label, details: c };
      }).filter((o) => o.value);
      setCustomerOptions(options);
    } catch (err) {
      console.error("Error loading customers:", err);
      toast.error(typeof err === "string" ? err : "Failed to load supplier options");
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, [resolvedTenantId]);

  const loadCustomerGroupOptions = useCallback(async () => {
    setLoadingCustomerGroups(true);
    try {
      const list = await fetchCustomerGroupList({});
      const arr = Array.isArray(list) ? list : [];
      const options = arr
        .map((g) => {
          const code = String(g.customerGroupCode ?? g.code ?? g.customerGroupcode ?? "").trim();
          const name = String(g.customerGroupName ?? g.name ?? "").trim();
          const id = g.id ?? g._id;
          const value = code || name || (id != null ? String(id) : "");
          const label = [code, name].filter(Boolean).join(" — ") || name || code || value;
          return value ? { value, label, raw: g } : null;
        })
        .filter(Boolean);
      setCustomerGroupSelectOptions(options);
    } catch (err) {
      console.error("Error loading customer groups:", err);
      toast.error(typeof err === "string" ? err : "Failed to load supplier groups");
      setCustomerGroupSelectOptions([]);
    } finally {
      setLoadingCustomerGroups(false);
    }
  }, []);

  useEffect(() => {
    loadSkuOptions();
    loadCustomerOptions();
    loadCustomerGroupOptions();
  }, [loadSkuOptions, loadCustomerOptions, loadCustomerGroupOptions]);

  // Load SKU details for edit mode
  useEffect(() => {
    if (isEdit && formData.skuid && !formData.description) {
      // If editing and we have skuid but no description, try to load it
      fetchDataAndPatchFields("skuId", formData.skuid)
        .then((skuDetails) => {
          if (skuDetails) {
            setFormData((prev) => ({
              ...prev,
              description: skuDetails.description || "",
            }));
          }
        })
        .catch((err) => {
          console.error("Error fetching SKU details for edit:", err);
        });
    }
  }, [isEdit, formData.skuid, formData.description]);

  /** Customer select: loaded rows plus current value if missing (e.g. edit payload uses id or name only) */
  const customerOptionsForSelect = useMemo(() => {
    const opts = [...customerOptions];
    const v = String(formData.customer || "").trim();
    if (v && !opts.some((o) => String(o.value) === v)) {
      opts.unshift({ value: v, label: v, details: null });
    }
    return opts;
  }, [customerOptions, formData.customer]);

  /** Select options: API groups plus current value when editing legacy rows */
  const customerGroupOptionsForSelect = useMemo(() => {
    const opts = [...customerGroupSelectOptions];
    const v = String(formData.customerGroup || "").trim();
    if (v && !opts.some((o) => String(o.value) === v)) {
      opts.unshift({ value: v, label: v, raw: null });
    }
    return opts;
  }, [customerGroupSelectOptions, formData.customerGroup]);

  const resolveGroupValueFromCustomer = useCallback((groupFromCustomer, prevGroup) => {
    const gStr = groupFromCustomer != null ? String(groupFromCustomer).trim() : "";
    if (!gStr) return prevGroup;
    const byCode = customerGroupSelectOptions.find(
      (o) => String(o.raw?.customerGroupCode ?? o.raw?.code ?? "").trim() === gStr
    );
    const byName = customerGroupSelectOptions.find(
      (o) => String(o.raw?.customerGroupName ?? o.raw?.name ?? "").trim() === gStr
    );
    const byValue = customerGroupSelectOptions.find((o) => String(o.value) === gStr);
    return (byCode || byName || byValue)?.value ?? gStr;
  }, [customerGroupSelectOptions]);

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    const opt = customerOptionsForSelect.find((o) => String(o.value) === String(customerId));
    const groupFromCustomer = opt?.details?.customerGroup;
    setFormData((prev) => ({
      ...prev,
      customer: customerId,
      customerGroup: !customerId
        ? ""
        : resolveGroupValueFromCustomer(groupFromCustomer, prev.customerGroup),
    }));
    setErrors((prev) => ({ ...prev, customer: "", customerGroup: "" }));
  };

  const handleSkuChange = async (e) => {
    const selectedSkuId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      skuid: selectedSkuId,
      description: "", // Clear description initially
    }));
    setErrors((prev) => ({ ...prev, skuid: "", description: "" }));

    if (selectedSkuId) {
      try {
        // Fetch SKU details to auto-fill description
        const skuDetails = await fetchDataAndPatchFields("skuId", selectedSkuId);
        if (skuDetails) {
          setFormData((prev) => ({
            ...prev,
            description: skuDetails.description || "",
          }));
        } else {
          toast.error("Could not find SKU details");
          setFormData((prev) => ({
            ...prev,
            skuid: "",
          }));
        }
      } catch (err) {
        console.error("Error fetching SKU details:", err);
        toast.error("Failed to load SKU description");
        setFormData((prev) => ({
          ...prev,
          skuid: "",
        }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.skuid.trim()) {
      newErrors.skuid = "SKU ID is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }
    if (!formData.originalPrice) {
      newErrors.originalPrice = "Purchase price is required";
    } else if (isNaN(parseFloat(formData.originalPrice)) || parseFloat(formData.originalPrice) < 0) {
      newErrors.originalPrice = "Please enter a valid price";
    }
    if (!formData.sellingPrice) {
      newErrors.sellingPrice = "Selling price is required";
    } else if (isNaN(parseFloat(formData.sellingPrice)) || parseFloat(formData.sellingPrice) < 0) {
      newErrors.sellingPrice = "Please enter a valid price";
    }
    if (!formData.customer.trim()) {
      newErrors.customer = "Supplier is required";
    }
    if (!String(formData.customerGroup || "").trim()) {
      newErrors.customerGroup = "Supplier group is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updatePricingFoundation(editData.id || editData._id, formData);
        toast.success("Pricing foundation updated successfully!");
      } else {
        await createPricingFoundation(formData);
        toast.success("Pricing foundation created successfully!");
      }
      
      // Reset form if not edit
      if (!isEdit) {
        setFormData({
          skuid: "",
          description: "",
          originalPrice: "",
          sellingPrice: "",
          customer: "",
          customerGroup: "",
        });
        setErrors({});
      }
      
      // Call onSuccess callback to refresh parent list
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (err) {
      console.error("Error saving pricing foundation:", err);
      toast.error(err || `Failed to ${isEdit ? 'update' : 'create'} pricing foundation`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="create-pricing-foundation-card border-0">
      <Card.Header className="bg-white border-bottom">
        <h5 className="mb-0">
          {readOnly ? "View Pricing Foundation" : isEdit ? "Edit Pricing Foundation" : "Create Pricing Foundation"}
        </h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">SKU ID</Form.Label>
                <Form.Select
                  value={formData.skuid}
                  onChange={handleSkuChange}
                  disabled={loadingSkus || isEdit || readOnly}
                  isInvalid={!!errors.skuid}
                  required
                >
                  <option value="">{loadingSkus ? "Loading SKUs..." : PLACEHOLDER_SELECT}</option>
                  {skuOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {errors.skuid && <Form.Control.Feedback type="invalid" className="d-block">{errors.skuid}</Form.Control.Feedback>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Description</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Auto-filled from SKU selection"
                  required
                  readOnly
                  disabled={readOnly}
                  isInvalid={!!errors.description}
                />
                {errors.description && <Form.Control.Feedback type="invalid" className="d-block">{errors.description}</Form.Control.Feedback>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Purchase Price</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="0"
                  value={formData.originalPrice}
                  onChange={(e) => handleInputChange("originalPrice", e.target.value)}
                  placeholder="Enter purchase price"
                  required
                  readOnly={readOnly}
                  disabled={readOnly}
                  isInvalid={!!errors.originalPrice}
                />
                {errors.originalPrice && <Form.Control.Feedback type="invalid" className="d-block">{errors.originalPrice}</Form.Control.Feedback>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Retail Price</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  min="0"
                  value={formData.sellingPrice}
                  onChange={(e) => handleInputChange("sellingPrice", e.target.value)}
                  placeholder="Enter retail price"
                  required
                  readOnly={readOnly}
                  disabled={readOnly}
                  isInvalid={!!errors.sellingPrice}
                />  
                {errors.sellingPrice && <Form.Control.Feedback type="invalid" className="d-block">{errors.sellingPrice}</Form.Control.Feedback>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Supplier</Form.Label>
                <Form.Select
                  value={formData.customer}
                  onChange={handleCustomerChange}
                  disabled={loadingCustomers || readOnly}
                  isInvalid={!!errors.customer}
                  required
                >
                  <option value="">{loadingCustomers ? "Loading suppliers..." : PLACEHOLDER_SELECT}</option>
                  {customerOptionsForSelect.map((option) => (
                    <option key={`${option.value}-${option.label}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {errors.customer && <Form.Control.Feedback type="invalid" className="d-block">{errors.customer}</Form.Control.Feedback>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="required">Supplier group</Form.Label>
                <Form.Select
                  value={formData.customerGroup}
                  onChange={(e) => handleInputChange("customerGroup", e.target.value)}
                  disabled={loadingCustomerGroups || readOnly}
                  isInvalid={!!errors.customerGroup}
                  required
                >
                  <option value="">
                    {loadingCustomerGroups ? "Loading supplier groups..." : PLACEHOLDER_SELECT}
                  </option>
                  {customerGroupOptionsForSelect.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {errors.customerGroup && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {errors.customerGroup}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly && (
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Pricing Foundation" : "Create Pricing Foundation")}
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}